import React from 'react';
import { Eye, Globe, User, Calendar, Copy, ExternalLink, MoreVertical, Bookmark, Lock, Network, Loader2, MapPin } from 'lucide-react';
import Table, { Column, SortDirection, TableCell, TableRow } from '@/components/common/ui/Table';
import { DataTypeBadge, PriorityBadge } from '@/components/common/ui/PriorityStatusBadges';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator
} from '@/components/common/ui/DropdownMenu';

export type SortField = 'priority' | 'data_type' | 'url' | 'username' | 'log_date' | 'ip' | 'location';
export type PriorityLevel = 'critical' | 'high' | 'medium' | 'low';

export interface SearchResult {
    data_type?: string | null;
    url?: string | null;
    log_id?: number | null;
    username?: string | null;
    password?: string | null;
    log_date?: string | null;
    hostname?: string | null;
    ip?: string | null;
    malware_path?: string | null;
    anti_virus?: string | null;
    _source_endpoint?: string | null;
    country_code?: string | null;
    country?: string | null;
    priority?: PriorityLevel;
    originalIndex: number;
}

interface SearchResultsTableProps {
    results: SearchResult[];
    onSort?: (field: SortField, direction: SortDirection) => void;
    sortField?: SortField;
    sortDirection?: SortDirection;
    onRowClick: (threat: SearchResult) => void;
    canSaveIncidents?: boolean;
    onSaveThreat?: (threat: SearchResult) => void;
    className?: string;
    isLoading?: boolean;
    activeFilter?: string | null;
}

const URLDisplay: React.FC<{ url?: string | null; hostname?: string | null }> = ({ url, hostname }) => {
    const displayUrl = url || hostname || '-';
    if (displayUrl === '-') return <span className="text-slate-600 font-mono text-[11px]">-</span>;

    const formatUrl = (url: string) => {
        try { return new URL(url).hostname; } catch { return url; }
    };

    const truncateUrl = (url: string, maxLength: number = 40) => {
        if (url.length <= maxLength) return url;
        return url.substring(0, maxLength) + '...';
    };

    const copyToClipboard = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(displayUrl);
    };

    const openUrl = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="flex items-center gap-2 min-w-0 max-w-sm group">
            <div className="flex items-center gap-2 min-w-0 flex-1">
                <Globe className="h-3 w-3 text-slate-500 flex-shrink-0" />
                <span className="font-mono text-[11px] text-slate-300 truncate transition-colors group-hover:text-white" title={displayUrl}>
                    {truncateUrl(formatUrl(displayUrl))}
                </span>
            </div>
            <div className="flex gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={copyToClipboard} className="p-1 text-slate-500 hover:text-[#50A970] transition-colors" title="Copy URL">
                    <Copy className="h-3 w-3" />
                </button>
                {url && (url.startsWith('http://') || url.startsWith('https://')) && (
                    <button onClick={openUrl} className="p-1 text-slate-500 hover:text-[#00FFFF] transition-colors" title="Open URL">
                        <ExternalLink className="h-3 w-3" />
                    </button>
                )}
            </div>
        </div>
    );
};

const UsernameDisplay: React.FC<{ username?: string | null }> = ({ username }) => {
    if (!username) return <span className="text-slate-600 font-mono text-[11px]">-</span>;
    const maskUsername = (username: string): string => {
        if (username.length <= 3) return '***';
        return username.substring(0, 2) + '***' + username.substring(username.length - 1);
    };
    return (
        <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-slate-500" />
            <span className="font-mono text-[11px] text-slate-300">{maskUsername(username)}</span>
        </div>
    );
};

const DateDisplay: React.FC<{ date?: string | null }> = ({ date }) => {
    if (!date) return <span className="text-slate-600 font-mono text-[11px]">-</span>;
    const formatDate = (dateStr: string): string => {
        try { return new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); }
        catch { return 'Invalid Date'; }
    };
    return (
        <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3 text-slate-500" />
            <span className="font-mono text-[11px] text-slate-400">{formatDate(date)}</span>
        </div>
    );
};

const IPDisplay: React.FC<{ ip?: string | null }> = ({ ip }) => {
    if (!ip) return <span className="text-slate-600 font-mono text-[11px]">-</span>;
    const copyToClipboard = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(ip);
    };
    return (
        <div className="flex items-center gap-2 group">
            <div className="flex items-center gap-2">
                <Network className="h-3 w-3 text-[#00FFFF] opacity-70 flex-shrink-0" />
                <span className="font-mono text-[11px] text-[#00FFFF] tracking-widest">{ip}</span>
            </div>
            <button onClick={copyToClipboard} className="p-1 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-[#00FFFF] bg-transparent" title="Copy IP">
                <Copy className="h-3 w-3" />
            </button>
        </div>
    );
};

const LocationDisplay: React.FC<{ countryCode?: string | null; country?: string | null }> = ({ countryCode, country }) => {
    if (!countryCode && !country) return <span className="text-slate-600 font-mono text-[11px]">-</span>;
    const displayText = country || countryCode;
    return (
        <div className="flex items-center gap-2">
            <MapPin className="h-3 w-3 text-purple-500 opacity-70 flex-shrink-0" />
            <span className="font-mono text-[11px] text-slate-300 truncate max-w-[120px]" title={displayText!}>
                {displayText}
            </span>
        </div>
    );
};

const SearchResultsTable: React.FC<SearchResultsTableProps> = ({
    results, onSort, sortField = 'log_date', sortDirection = 'desc', onRowClick,
    canSaveIncidents = false, onSaveThreat, className = '', isLoading = false, activeFilter = null
}) => {
    const getPriority = (dataType: string | null | undefined): PriorityLevel => {
        if (!dataType) return 'medium';
        return dataType.toLowerCase() === 'stealer' ? 'high' : 'medium';
    };

    const getDataTypeBadgeValue = (dataType: string | null | undefined): 'stealer' | 'combolist' => {
        if (!dataType) return 'combolist';
        return dataType.toLowerCase() === 'stealer' ? 'stealer' : 'combolist';
    };

    const columns: Column[] = [
        { id: 'priority', label: 'Priority', sortable: true, width: '100px' },
        { id: 'data_type', label: 'Type', sortable: true, width: '110px' },
        { id: 'ip', label: 'IP Address', sortable: true, width: '150px' },
        { id: 'location', label: 'Location', sortable: true, width: '140px' },
        { id: 'url', label: 'URL/Domain', sortable: true, minWidth: '250px' },
        { id: 'username', label: 'Username', sortable: true, width: '130px' },
        { id: 'log_date', label: 'Log Date', sortable: true, width: '120px' },
        { id: 'actions', label: 'Actions', sortable: false, width: '70px' },
    ];

    const handleSort = (columnId: string) => {
        if (!onSort) return;
        const field = columnId as SortField;
        const newDirection: SortDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        onSort(field, newDirection);
    };

    if (results.length === 0) {
        if (isLoading) {
            return (
                <div className="flex flex-col items-center justify-center p-16 bg-[#0A0B0E] border-t border-[#1A1D24]">
                    <Loader2 className="h-10 w-10 text-[#00FFFF] animate-spin mb-4" />
                    <div className="text-[#00FFFF] font-mono text-[11px] uppercase tracking-widest animate-pulse">
                        Scanning Deep Web for {activeFilter ? activeFilter.toUpperCase() : 'Regional'} Matches...
                    </div>
                </div>
            );
        }
        return (
            <div className="text-center p-16 bg-[#0A0B0E] border-t border-[#1A1D24]">
                <div className="text-[#FF3333] font-mono text-[11px] uppercase tracking-widest">No Matching Threat Logs Found</div>
                <div className="text-slate-500 font-mono text-[10px] mt-2">The requested filter returned 0 records in the currently loaded dataset.</div>
            </div>
        );
    }

    return (
        <div className={`overflow-x-auto custom-scrollbar cyber-table-override ${className}`}>
            <Table columns={columns} onSort={handleSort} sortColumn={sortField} sortDirection={sortDirection}>
                {results.map((result, index) => {
                    const uniqueKey = `result-${result.originalIndex}-${result.log_id || index}`;
                    return (
                        <TableRow
                            key={uniqueKey}
                            onClick={() => onRowClick(result)}
                            className="bg-[#0A0B0E] cursor-pointer transition-colors border-b border-[#1A1D24] hover:bg-[#12151C] hover:shadow-[inset_2px_0_0_#50A970]"
                        >
                            <TableCell><PriorityBadge priority={getPriority(result.data_type)} /></TableCell>
                            <TableCell><DataTypeBadge dataType={getDataTypeBadgeValue(result.data_type)} /></TableCell>
                            <TableCell><IPDisplay ip={result.ip} /></TableCell>
                            <TableCell><LocationDisplay countryCode={result.country_code} country={result.country} /></TableCell>
                            <TableCell><URLDisplay url={result.url} hostname={result.hostname} /></TableCell>
                            <TableCell><UsernameDisplay username={result.username} /></TableCell>
                            <TableCell><DateDisplay date={result.log_date} /></TableCell>
                            <TableCell>
                                <DropdownMenu>
                                    <DropdownMenuTrigger className="text-slate-500 hover:text-[#50A970] p-1 rounded transition-colors focus:outline-none">
                                        <MoreVertical className="h-4 w-4" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="bg-[#0A0B0E] border border-[#1A1D24] text-slate-300 font-mono text-[11px] shadow-2xl z-[9999]">
                                        <DropdownMenuItem onClick={() => onRowClick(result)} className="hover:bg-[#161A23] hover:text-white cursor-pointer transition-colors">
                                            <Eye className="h-3.5 w-3.5 mr-2 text-slate-400" /> View Details
                                        </DropdownMenuItem>
                                        {canSaveIncidents ? (
                                            <>
                                                <DropdownMenuSeparator className="bg-[#1A1D24]" />
                                                <DropdownMenuItem onClick={() => onSaveThreat && onSaveThreat(result)} className="hover:bg-[#161A23] hover:text-[#50A970] cursor-pointer transition-colors">
                                                    <Bookmark className="h-3.5 w-3.5 mr-2 text-[#50A970]" /> Save Threat
                                                </DropdownMenuItem>
                                            </>
                                        ) : (
                                            <>
                                                <DropdownMenuSeparator className="bg-[#1A1D24]" />
                                                <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed">
                                                    <Lock className="h-3.5 w-3.5 mr-2 text-slate-500" /> Save Threat (Upgrade)
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        {result.url && (
                                            <>
                                                <DropdownMenuSeparator className="bg-[#1A1D24]" />
                                                <DropdownMenuItem onClick={() => navigator.clipboard.writeText(result.url!)} className="hover:bg-[#161A23] hover:text-white cursor-pointer transition-colors">
                                                    <Copy className="h-3.5 w-3.5 mr-2 text-slate-400" /> Copy URL
                                                </DropdownMenuItem>
                                                {(result.url.startsWith('http://') || result.url.startsWith('https://')) && (
                                                    <DropdownMenuItem onClick={() => window.open(result.url!, '_blank', 'noopener,noreferrer')} className="hover:bg-[#161A23] hover:text-[#00FFFF] cursor-pointer transition-colors">
                                                        <ExternalLink className="h-3.5 w-3.5 mr-2 text-[#00FFFF]" /> Open URL
                                                    </DropdownMenuItem>
                                                )}
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </Table>

            <style dangerouslySetInnerHTML={{
                __html: `
                .cyber-table-override table { background: transparent !important; }
                .cyber-table-override th { 
                    background-color: #12151C !important; 
                    color: #94a3b8 !important; 
                    border-bottom: 1px solid #1A1D24 !important; 
                    font-family: monospace !important; 
                    font-size: 10px !important; 
                    text-transform: uppercase !important; 
                    letter-spacing: 0.1em !important; 
                }
                .cyber-table-override tbody { background-color: transparent !important; }
                .cyber-table-override tbody tr { background-color: #0A0B0E !important; border-bottom: 1px solid #1A1D24 !important; }
                .cyber-table-override tbody tr:hover { background-color: #12151C !important; }
                .cyber-table-override td { background-color: transparent !important; border: none !important; }
            `}} />
        </div>
    );
};

export default SearchResultsTable;